(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const isValidEmail = (value) => {
    if (!value) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  const isValidPhone = (value) => {
    if (!value) return true;
    const digits = value.replace(/[^\d]/g, "");
    return digits.length >= 8 && digits.length <= 15;
  };

  const setMessage = (form, type, message) => {
    const box = qs("[data-aura-form-message]", form);
    if (!box) return;
    box.textContent = message;
    box.classList.remove("is-success", "is-error", "is-visible");
    if (message) box.classList.add("is-visible", type === "success" ? "is-success" : "is-error");
  };

  const getFormDataObject = (form) => {
    const fd = new FormData(form);
    const data = {};
    for (const [key, value] of fd.entries()) data[key] = String(value || "").trim();
    return data;
  };

  const buildWhatsAppMessage = (form, data) => {
    const prefix = form.dataset.whatsappPrefix || "Hello Aura team, I submitted an enquiry on the website. Details:";
    const lines = [
      prefix,
      "",
      `Enquiry Type: ${data.enquiry_type || "-"}`,
      `Name: ${data.name || "-"}`,
      `Phone: ${data.phone || "-"}`,
      `Email: ${data.email || "-"}`,
      `City: ${data.city || "-"}`,
      `Occasion / Requirement: ${data.occasion || "-"}`,
      `Timeline: ${data.timeline || "-"}`,
      `Message: ${data.message || "-"}`,
      "",
      `Page: ${data.page_url || window.location.href}`
    ];
    return lines.join("\n");
  };

  const openWhatsApp = (form, data) => {
    if (form.dataset.enableWhatsapp !== "true") return;
    const number = (form.dataset.whatsappNumber || "").replace(/[^\d]/g, "");
    if (!number) return;

    const url = `https://wa.me/${number}?text=${encodeURIComponent(buildWhatsAppMessage(form, data))}`;
    if (form.dataset.whatsappNewTab === "true") {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  const validateForm = (form) => {
    let valid = true;
    qsa(".aura-contact-form__field", form).forEach((wrap) => wrap.classList.remove("is-error"));

    qsa("[data-required='true']", form).forEach((field) => {
      if (!String(field.value || "").trim()) {
        const wrap = field.closest(".aura-contact-form__field");
        if (wrap) wrap.classList.add("is-error");
        valid = false;
      }
    });

    const email = qs("[data-email-field]", form);
    if (email && email.value.trim() && !isValidEmail(email.value)) {
      const wrap = email.closest(".aura-contact-form__field");
      if (wrap) wrap.classList.add("is-error");
      valid = false;
    }

    const phone = qs("[data-phone-field]", form);
    if (phone && phone.value.trim() && !isValidPhone(phone.value)) {
      const wrap = phone.closest(".aura-contact-form__field");
      if (wrap) wrap.classList.add("is-error");
      valid = false;
    }

    return valid;
  };

  const submitToAppsScript = async (form, data) => {
    const shouldSubmit = form.dataset.enableSheet === "true" || form.dataset.enableEmail === "true";
    const url = form.dataset.appsScriptUrl || "";
    if (!shouldSubmit || !url) return { skipped: true };

    const payload = new FormData();
    Object.entries(data).forEach(([key, value]) => payload.append(key, value));
    payload.append("enable_sheet", form.dataset.enableSheet === "true" ? "true" : "false");
    payload.append("enable_email", form.dataset.enableEmail === "true" ? "true" : "false");
    payload.append("email_subject", form.dataset.emailSubject || "New Aura website enquiry");
    payload.append("form_secret", form.dataset.formSecret || "");

    await fetch(url, {
      method: "POST",
      mode: "no-cors",
      body: payload,
      credentials: "omit"
    });

    return { ok: true };
  };

  const closeSelect = (select) => {
    const button = qs("[data-aura-custom-select-button]", select);
    select.classList.remove("is-open");
    if (button) button.setAttribute("aria-expanded", "false");
  };

  const openSelect = (select) => {
    qsa("[data-aura-custom-select]").forEach((other) => {
      if (other !== select) closeSelect(other);
    });
    const button = qs("[data-aura-custom-select-button]", select);
    select.classList.add("is-open");
    if (button) button.setAttribute("aria-expanded", "true");
  };

  const setCustomSelectValue = (select, value) => {
    if (!select || !value) return false;
    const input = qs("[data-aura-custom-select-input]", select);
    const label = qs("[data-aura-custom-select-label]", select);
    const options = qsa("[data-aura-custom-select-option]", select);
    const normalized = String(value).toLowerCase().trim();

    const option = options.find((item) => {
      const optionValue = String(item.dataset.value || item.textContent || "").toLowerCase().trim();
      return optionValue === normalized;
    });

    if (!option || option.disabled) return false;

    const finalValue = option.dataset.value || option.textContent.trim();
    if (input) input.value = finalValue;
    if (label) label.textContent = option.textContent.trim();

    options.forEach((item) => item.classList.remove("is-active"));
    option.classList.add("is-active");

    const wrap = select.closest(".aura-contact-form__field");
    if (wrap) wrap.classList.remove("is-error");

    return true;
  };

  const initCustomSelect = (select) => {
    if (select.dataset.auraReady === "true") return;
    select.dataset.auraReady = "true";

    const button = qs("[data-aura-custom-select-button]", select);
    const options = qsa("[data-aura-custom-select-option]", select);

    if (!button) return;

    button.addEventListener("click", (event) => {
      event.preventDefault();
      select.classList.contains("is-open") ? closeSelect(select) : openSelect(select);
    });

    options.forEach((option) => {
      option.addEventListener("click", (event) => {
        event.preventDefault();
        if (option.disabled) return;
        setCustomSelectValue(select, option.dataset.value || option.textContent.trim());
        closeSelect(select);
      });
    });

    document.addEventListener("click", (event) => {
      if (!select.contains(event.target)) closeSelect(select);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeSelect(select);
    });
  };

  const applyEnquiryParam = (form) => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("type") || params.get("enquiry") || params.get("enquiry_type");
    if (!requested) return;

    const enquirySelect = qs("[data-aura-enquiry-select]", form);
    if (enquirySelect) setCustomSelectValue(enquirySelect, decodeURIComponent(requested));
  };

  const initForm = (form) => {
    if (form.dataset.auraReady === "true") return;
    form.dataset.auraReady = "true";

    const pageUrl = qs("[data-aura-page-url]", form);
    if (pageUrl) pageUrl.value = window.location.href;

    qsa("[data-aura-custom-select]", form).forEach(initCustomSelect);
    applyEnquiryParam(form);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      setMessage(form, "", "");

      if (!validateForm(form)) {
        setMessage(form, "error", "Please check the highlighted fields.");
        return;
      }

      const button = qs("[data-aura-submit]", form);
      if (button) {
        button.classList.add("is-loading");
        button.disabled = true;
        button.dataset.originalText = button.textContent;
        button.textContent = "Sending...";
      }

      const data = getFormDataObject(form);
      data.page_url = window.location.href;

      try {
        await submitToAppsScript(form, data);
        setMessage(form, "success", "Thank you. Your enquiry has been prepared successfully.");
        openWhatsApp(form, data);

        if (form.dataset.enableWhatsapp !== "true") {
          form.reset();
        }
      } catch (error) {
        console.error(error);
        setMessage(form, "error", "Something went wrong. Please try again or contact us on WhatsApp.");
      } finally {
        if (button) {
          button.classList.remove("is-loading");
          button.disabled = false;
          button.textContent = button.dataset.originalText || "Send enquiry";
        }
      }
    });
  };

  document.addEventListener("DOMContentLoaded", () => {
    qsa("[data-aura-contact-form]").forEach(initForm);
  });

  document.addEventListener("shopify:section:load", (event) => {
    qsa("[data-aura-contact-form]", event.target).forEach(initForm);
  });
})();
